import React, { useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { Typography, Box, Divider, List, ListItem, ListItemIcon, ListItemText, Paper, Fade, Grow, TextField, IconButton, Snackbar, Alert, Button } from '@mui/material';
import axios from 'axios';
import { Person as PersonIcon, Email as EmailIcon, Work as WorkIcon, LocationOn as LocationIcon, Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { textTransform } from '../../node_modules/@mui/system/index';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Profile = () => {
  const doNotPerformCustomerFiltration = localStorage.getItem('role') === 'Broker';

  const userName = localStorage.getItem('userName');
  const email = localStorage.getItem('email');
  const role = localStorage.getItem('role');
  const clientID = localStorage.getItem('clientId');
  const brokerID = localStorage.getItem('brokerID');

  const [clientDetails, setClientDetails] = useState({
    customerID: '',
    clientName: '',
    primaryContactNumber: '',
    secondaryContactNumber: '',
    pointOfContact: '',
    secondaryPointOfContact: '',
    primaryEmail: '',
    secondaryEmail: '',
    address: '',
  });

  const [brokerDetails, setBrokerDetails] = useState({
    brokerID: '',
    brokerName: '',
    primaryContactNumber: '',
    primaryContact: '',
    primaryEmail: '',
    unitNumber: '',
    streetNumber: '',
    streetName: '',
    city: '',
    province: '',
    country: '',
    postalCode: '',
  });

  const [editMode, setEditMode] = useState(false);
  const [editedDetails, setEditedDetails] = useState({ ...clientDetails });
  const [editedBrokerDetails, setEditedBrokerDetails] = useState({ ...brokerDetails });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    if (clientID && !doNotPerformCustomerFiltration) {
      fetchClientDetails(clientID);
    }
  }, [clientID, doNotPerformCustomerFiltration]);

  useEffect(() => {
    if (brokerID && doNotPerformCustomerFiltration) {
      fetchBrokerDetails(brokerID);
    }
  }, [brokerID, doNotPerformCustomerFiltration]);

  const fetchClientDetails = async clientID => {
    try {
      const response = await axios.get(`${BACKEND_URL}/v1/client`, {
        params: { clientID },
      });

      if (response.data && response.data.length > 0) {
        const client = response.data[0];
        setClientDetails({
          customerID: client.customerID,
          clientName: client.clientName || client.name || 'N/A',
          primaryContactNumber: client.primaryContactNumber || 'N/A',
          secondaryContactNumber: client.secondaryContactNumber || 'N/A',
          pointOfContact: client.pointOfContact || 'N/A',
          secondaryPointOfContact: client.secondaryPointOfContact || 'N/A',
          primaryEmail: client.primaryEmail || 'N/A',
          secondaryEmail: client.secondaryEmail || 'N/A',
          address: client.address || 'N/A',
        });
        setEditedDetails({
          customerID: client.customerID,
          clientName: client.clientName || client.name || 'N/A',
          primaryContactNumber: client.primaryContactNumber || 'N/A',
          secondaryContactNumber: client.secondaryContactNumber || 'N/A',
          pointOfContact: client.pointOfContact || 'N/A',
          secondaryPointOfContact: client.secondaryPointOfContact || 'N/A',
          primaryEmail: client.primaryEmail || 'N/A',
          secondaryEmail: client.secondaryEmail || 'N/A',
          address: client.address || 'N/A',
        });
      } else {
        console.error('No client details found for the given clientID');
      }
    } catch (error) {
      console.error('Error retrieving client details:', error);
    }
  };

  const fetchBrokerDetails = async brokerID => {
    try {
      const response = await axios.get(`${BACKEND_URL}/v1/brokers`);
      const brokersData = response.data;
      const filteredBrokers = brokersData.filter(broker => broker.id === brokerID);

      if (filteredBrokers && filteredBrokers.length > 0) {
        const broker = filteredBrokers[0];
        setBrokerDetails({
          brokerID: brokerID,
          brokerName: broker.brokerName || 'N/A',
          primaryContactNumber: broker.primaryContactNumber || 'N/A',
          primaryContact: broker.primaryContact || 'N/A',
          primaryEmail: broker.primaryEmail || 'N/A',
          unitNumber: broker.unitNumber || 'N/A',
          streetNumber: broker.streetNumber || 'N/A',
          streetName: broker.streetName || 'N/A',
          city: broker.city || 'N/A',
          country: broker.country || 'N/A',
          province: broker.province || 'N/A',
          postalCode: broker.postalCode || 'N/A',
        });
        setEditedBrokerDetails({
          brokerID: broker.id,
          brokerName: broker.brokerName || 'N/A',
          primaryContactNumber: broker.primaryContactNumber || 'N/A',
          primaryContact: broker.primaryContact || 'N/A',
          primaryEmail: broker.primaryEmail || 'N/A',
          unitNumber: broker.unitNumber || 'N/A',
          streetNumber: broker.streetNumber || 'N/A',
          streetName: broker.streetName || 'N/A',
          city: broker.city || 'N/A',
          country: broker.country || 'N/A',
          province: broker.province || 'N/A',
          postalCode: broker.postalCode || 'N/A',
        });
      } else {
        console.error('No broker details found for the given brokerID');
      }
    } catch (error) {
      console.error('Error retrieving broker details:', error);
    }
  };

  const handleEditClick = () => {
    setEditMode(true);
    const parsedAddress = parseAddress(clientDetails.address);
    setEditedDetails({
      ...clientDetails,
      ...parsedAddress,
    });
  };

  const handleEditBrokerClick = () => {
    setEditMode(true);
    setEditedBrokerDetails({ ...brokerDetails });
  };

  const handleCancelClick = () => {
    setEditMode(false);
    setEditedDetails({ ...clientDetails });
  };

  const handleBrokerCancelClick = () => {
    setEditMode(false);
    setEditedBrokerDetails({ ...brokerDetails });
  };

  const handleSaveClick = async () => {
    try {
      const updatedAddress = [editedDetails.unitNumber, editedDetails.streetNumber, editedDetails.streetName, editedDetails.city, editedDetails.province, editedDetails.country, editedDetails.postalCode].filter(Boolean).join(',');

      const updatedClientData = {
        clientID: clientID,
        customerID: clientDetails.customerID,
        clientName: clientDetails.clientName,
        address: updatedAddress,
        primaryContactNumber: editedDetails.primaryContactNumber,
        secondaryContactNumber: editedDetails.secondaryContactNumber,
        pointOfContact: editedDetails.pointOfContact,
        primaryEmail: editedDetails.primaryEmail,
        secondaryEmail: editedDetails.secondaryEmail,
        secondaryPointOfContact: editedDetails.secondaryPointOfContact,
      };

      const response = await fetch(`${BACKEND_URL}/v1/client`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedClientData),
      });

      if (response.ok) {
        setClientDetails(updatedClientData);
        setEditMode(false);
        setSnackbarMessage('Client details updated successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        throw new Error('Failed to update client details.');
      }
    } catch (error) {
      console.error('Error updating client details:', error);
      setSnackbarMessage('Failed to update client details. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleBrokerSaveClick = async () => {
    try {
      const updatedBrokerData = {
        brokerID: brokerID,
        brokerName: brokerDetails.brokerName,
        primaryContactNumber: editedBrokerDetails.primaryContactNumber,
        primaryContact: editedBrokerDetails.primaryContact,
        primaryEmail: editedBrokerDetails.primaryEmail,
        unitNumber: editedBrokerDetails.unitNumber,
        streetNumber: editedBrokerDetails.streetNumber,
        streetName: editedBrokerDetails.streetName,
        city: editedBrokerDetails.city,
        province: editedBrokerDetails.province,
        country: editedBrokerDetails.country,
        postalCode: editedBrokerDetails.postalCode,
      };

      const response = await fetch(`${BACKEND_URL}/v1/brokers/${brokerID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedBrokerData),
      });

      if (response.ok) {
        setBrokerDetails(updatedBrokerData);
        setEditMode(false);
        setSnackbarMessage('Broker details updated successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        throw new Error('Failed to update broker details.');
      }
    } catch (error) {
      console.error('Error updating broker details:', error);
      setSnackbarMessage('Failed to update broker details. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleInputChange = event => {
    const { name, value } = event.target;
    setEditedDetails(prevDetails => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  const handleBrokerInputChange = event => {
    const { name, value } = event.target;
    setEditedBrokerDetails(prevDetails => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  const parseAddress = addressString => {
    if (!addressString) return {};
    const parts = addressString.split(',');
    return {
      unitNumber: parts[0] || '',
      streetNumber: parts[1] || '',
      streetName: parts[2] || '',
      city: parts[3] || '',
      province: parts[4] || '',
      country: parts[5] || '',
      postalCode: parts[6] || '',
    };
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };
const navigate = useNavigate();
  const handleCreateEmployeeLogin = () => {
  navigate('/create-employee-login');
};

  const { unitNumber, streetNumber, streetName, city, province, country, postalCode } = doNotPerformCustomerFiltration ? brokerDetails : parseAddress(clientDetails.address);

  return (
    <Fade in={true} timeout={1000}>
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          overflow: 'hidden',
        }}
      >
        {/* Sidebar */}
        <Grow in={true} timeout={1500}>
          <Paper
            elevation={3}
            sx={{
              width: 300,
              backgroundColor: '#31478F',
              color: 'white',
              padding: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 0,
              boxShadow: '4px 0 10px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
              {userName}
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 3 }}>
              {role}
            </Typography>
            <Divider sx={{ width: '100%', backgroundColor: 'white', marginBottom: 3 }} />
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              Welcome to your profile! Here you can view and manage your details.
            </Typography>
           {role === 'Super Admin' && (
  <Button
    variant="contained"
    color="primary"
    onClick={handleCreateEmployeeLogin}
    sx={{ marginTop: 2, backgroundColor: 'transparent', color: 'white', border: '1px solid white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
  >
    Create Employee Login
  </Button>
)}
          </Paper>
        </Grow>

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            padding: 4,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '0.4em',
            },
            '&::-webkit-scrollbar-track': {
              boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
              webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,.1)',
              outline: '1px solid slategrey',
            },
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              marginBottom: 4,
              color: 'primary.main',
              animation: 'fadeIn 1s ease-in-out',
            }}
          >
            Profile Details
          </Typography>

          {/* User Details */}
          <Grow in={true} timeout={2000}>
            <Paper
              elevation={3}
              sx={{
                marginBottom: 4,
                padding: 3,
                borderRadius: 2,
                backgroundColor: 'background.paper',
                transition: 'box-shadow 0.3s ease-in-out',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 2 }}>
                <PersonIcon sx={{ marginRight: 1, verticalAlign: 'middle' }} />
                User Information
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText primary="Email" secondary={email} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <WorkIcon />
                  </ListItemIcon>
                  <ListItemText primary="Role" secondary={role} />
                </ListItem>
              </List>
            </Paper>
          </Grow>

           {role !== 'Employee' && (
      <>

          {/* Client Details */}
          {!doNotPerformCustomerFiltration && (
            <Grow in={true} timeout={2500}>
              <Paper
                elevation={3}
                sx={{
                  marginBottom: 4,
                  padding: 3,
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  transition: 'box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 2 }}>
                    <WorkIcon sx={{ marginRight: 1, verticalAlign: 'middle' }} />
                    Account Details
                  </Typography>
                  {editMode ? (
                    <Box>
                      <IconButton onClick={handleSaveClick} color="primary">
                        <SaveIcon />
                      </IconButton>
                      <IconButton onClick={handleCancelClick} color="secondary">
                        <CancelIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <IconButton onClick={handleEditClick} color="primary">
                      <EditIcon />
                    </IconButton>
                  )}
                </Box>
                <List>
                  <ListItem>
                    <ListItemText primary="Account" secondary={clientID === '100' ? `${clientDetails.clientName}` : `${clientID}-${clientDetails.customerID}-${clientDetails.clientName}`} />
                  </ListItem>
                  <ListItem>
                    {editMode ? (
                      <TextField fullWidth label="Primary Contact" name="pointOfContact" value={editedDetails.pointOfContact} onChange={handleInputChange} />
                    ) : (
                      <ListItemText primary="Primary Contact" secondary={clientDetails.pointOfContact} />
                    )}
                  </ListItem>
                  <ListItem>
                    {editMode ? (
                      <TextField fullWidth label="Secondary Contact" name="secondaryPointOfContact" value={editedDetails.secondaryPointOfContact} onChange={handleInputChange} />
                    ) : (
                      <ListItemText primary="Secondary Contact" secondary={clientDetails.secondaryPointOfContact} />
                    )}
                  </ListItem>
                  <ListItem>
                    {editMode ? <TextField fullWidth label="Primary Email" name="primaryEmail" value={editedDetails.primaryEmail} onChange={handleInputChange} /> : <ListItemText primary="Primary Email" secondary={clientDetails.primaryEmail} />}
                  </ListItem>
                  <ListItem>
                    {editMode ? (
                      <TextField fullWidth label="Secondary Email" name="secondaryEmail" value={editedDetails.secondaryEmail} onChange={handleInputChange} />
                    ) : (
                      <ListItemText primary="Secondary Email" secondary={clientDetails.secondaryEmail} />
                    )}
                  </ListItem>
                  <ListItem>
                    {editMode ? (
                      <TextField fullWidth label="Primary Contact Number" name="primaryContactNumber" value={editedDetails.primaryContactNumber} onChange={handleInputChange} />
                    ) : (
                      <ListItemText primary="Primary Contact Number" secondary={clientDetails.primaryContactNumber} />
                    )}
                  </ListItem>
                  <ListItem>
                    {editMode ? (
                      <TextField fullWidth label="Secondary Contact Number" name="secondaryContactNumber" value={editedDetails.secondaryContactNumber} onChange={handleInputChange} />
                    ) : (
                      <ListItemText primary="Secondary Contact Number" secondary={clientDetails.secondaryContactNumber} />
                    )}
                  </ListItem>
                </List>
              </Paper>
            </Grow>
          )}

          {/* Broker Details */}
          {doNotPerformCustomerFiltration && (
            <Grow in={true} timeout={2500}>
              <Paper
                elevation={3}
                sx={{
                  marginBottom: 4,
                  padding: 3,
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  transition: 'box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 2 }}>
                    <WorkIcon sx={{ marginRight: 1, verticalAlign: 'middle' }} />
                    Broker Information
                  </Typography>
                  {editMode ? (
                    <Box>
                      <IconButton onClick={handleBrokerSaveClick} color="primary">
                        <SaveIcon />
                      </IconButton>
                      <IconButton onClick={handleBrokerCancelClick} color="secondary">
                        <CancelIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <IconButton onClick={handleEditBrokerClick} color="primary">
                      <EditIcon />
                    </IconButton>
                  )}
                </Box>
                <List>
                  <ListItem>
                    <ListItemText primary="Broker Name" secondary={brokerDetails.brokerName} />
                  </ListItem>
                  <ListItem>
                    {editMode ? (
                      <TextField fullWidth label="Primary Contact" name="primaryContact" value={editedBrokerDetails.primaryContact} onChange={handleBrokerInputChange} />
                    ) : (
                      <ListItemText primary="Primary Contact" secondary={brokerDetails.primaryContact} />
                    )}
                  </ListItem>
                  <ListItem>
                    {editMode ? (
                      <TextField fullWidth label="Primary Email" name="primaryEmail" value={editedBrokerDetails.primaryEmail} onChange={handleBrokerInputChange} />
                    ) : (
                      <ListItemText primary="Primary Email" secondary={brokerDetails.primaryEmail} />
                    )}
                  </ListItem>
                  <ListItem>
                    {editMode ? (
                      <TextField fullWidth label="Primary Contact Number" name="primaryContactNumber" value={editedBrokerDetails.primaryContactNumber} onChange={handleBrokerInputChange} />
                    ) : (
                      <ListItemText primary="Primary Contact Number" secondary={brokerDetails.primaryContactNumber} />
                    )}
                  </ListItem>
                </List>
              </Paper>
            </Grow>
          )}

          {/* Address Details */}
          <Grow in={true} timeout={3000}>
            <Paper
              elevation={3}
              sx={{
                padding: 3,
                borderRadius: 2,
                backgroundColor: 'background.paper',
                transition: 'box-shadow 0.3s ease-in-out',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 2 }}>
                <LocationIcon sx={{ marginRight: 1, verticalAlign: 'middle' }} />
                Address Information
              </Typography>
              <List>
                <ListItem>
                  {editMode ? (
                    <TextField
                      fullWidth
                      label="Unit Number"
                      name="unitNumber"
                      value={doNotPerformCustomerFiltration ? editedBrokerDetails.unitNumber : editedDetails.unitNumber}
                      onChange={doNotPerformCustomerFiltration ? handleBrokerInputChange : handleInputChange}
                    />
                  ) : (
                    <ListItemText primary="Unit Number" secondary={unitNumber} />
                  )}
                </ListItem>
                <ListItem>
                  {editMode ? (
                    <TextField
                      fullWidth
                      label="Street Number"
                      name="streetNumber"
                      value={doNotPerformCustomerFiltration ? editedBrokerDetails.streetNumber : editedDetails.streetNumber}
                      onChange={doNotPerformCustomerFiltration ? handleBrokerInputChange : handleInputChange}
                    />
                  ) : (
                    <ListItemText primary="Street Number" secondary={streetNumber} />
                  )}
                </ListItem>
                <ListItem>
                  {editMode ? (
                    <TextField
                      fullWidth
                      label="Street Name"
                      name="streetName"
                      value={doNotPerformCustomerFiltration ? editedBrokerDetails.streetName : editedDetails.streetName}
                      onChange={doNotPerformCustomerFiltration ? handleBrokerInputChange : handleInputChange}
                    />
                  ) : (
                    <ListItemText primary="Street Name" secondary={streetName} />
                  )}
                </ListItem>
                <ListItem>
                  {editMode ? (
                    <TextField fullWidth label="City" name="city" value={doNotPerformCustomerFiltration ? editedBrokerDetails.city : editedDetails.city} onChange={doNotPerformCustomerFiltration ? handleBrokerInputChange : handleInputChange} />
                  ) : (
                    <ListItemText primary="City" secondary={city} />
                  )}
                </ListItem>
                <ListItem>
                  {editMode ? (
                    <TextField
                      fullWidth
                      label="Country"
                      name="country"
                      value={doNotPerformCustomerFiltration ? editedBrokerDetails.country : editedDetails.country}
                      onChange={doNotPerformCustomerFiltration ? handleBrokerInputChange : handleInputChange}
                    />
                  ) : (
                    <ListItemText primary="Country" secondary={country} />
                  )}
                </ListItem>
                <ListItem>
                  {editMode ? (
                    <TextField
                      fullWidth
                      label="State"
                      name="province"
                      value={doNotPerformCustomerFiltration ? editedBrokerDetails.province : editedDetails.province}
                      onChange={doNotPerformCustomerFiltration ? handleBrokerInputChange : handleInputChange}
                    />
                  ) : (
                    <ListItemText primary="State" secondary={province} />
                  )}
                </ListItem>
                <ListItem>
                  {editMode ? (
                    <TextField
                      fullWidth
                      label="Zip Code"
                      name="postalCode"
                      value={doNotPerformCustomerFiltration ? editedBrokerDetails.postalCode : editedDetails.postalCode}
                      onChange={doNotPerformCustomerFiltration ? handleBrokerInputChange : handleInputChange}
                    />
                  ) : (
                    <ListItemText primary="Zip Code" secondary={postalCode} />
                  )}
                </ListItem>
              </List>
            </Paper>
          </Grow>
            </>
    )}
        </Box>
        <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
};

export default Profile;